import { Fragment, useContext } from 'react';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import {
    isStatsFilter,
    isPriceFilter,
    isDimensionFilter,
    isWhiteGloveFilter,
    isFreeShippingFilter,
    constructStatsRange,
    isColorFilter,
    isDeliveredByFilter,
    isLocalPickupFilter,
    isRingSizeFilter,
} from '../SbSharedRefineMenu/sbSharedRefineMenuHelpers';
import { handleMinimumFractionDigits } from 'dibs-buyer-price/exports/FormattedPrice';
import { sbSharedRefineMenuMessages } from '../SbSharedRefineMenu/sbSharedRefineMenuMessages';
import { RING_SIZE_MM } from '../../constants/ringSize';

// components
import { FormattedNumber, FormattedMessage } from 'dibs-react-intl';
import { SearchTag } from 'dibs-elements/exports/SearchTag';

// styles
import styles from './SbRespAppliedFiltersItem.scss';

const rangeSeparator = sbSharedRefineMenuMessages.to;

const whiteGloveFilterMaxValue = filterValue => filterValue && filterValue.displayName;

const minMaxToAppliedFilterValue = (min, max) =>
    [min, ' ', rangeSeparator, ' ', max].map((comp, index) => (
        <Fragment key={index}>{comp}</Fragment>
    ));

const whiteGloveFilterValue = ({ currency, filterValue }) => {
    const min = (
        <FormattedNumber value={0} style="currency" currency={currency} minimumFractionDigits={0} />
    );

    const max = (
        <FormattedNumber
            value={whiteGloveFilterMaxValue(filterValue)}
            style="currency"
            currency={currency}
            minimumFractionDigits={0}
        />
    );

    return minMaxToAppliedFilterValue(min, max);
};

export const SbRespAppliedFiltersItem = props => {
    const {
        localizedFilterName,
        filterName,
        filterValue,
        onCloseClick,
        currency,
        availableValues,
        measurementUnit,
        ringMeasurementType,
    } = props;
    const displayName = get(filterValue, 'displayName', null);
    const urlLabel = get(filterValue, 'urlLabel', null);
    let localizedFilterValue = localizedFilterName;
    let value = displayName;
    let dimension = null;
    const { isSbTopNavVariant } = useContext(ServerVarsContext);

    if (isDimensionFilter(filterName)) {
        dimension = measurementUnit.toLowerCase();
    }

    if (isDeliveredByFilter(filterName)) {
        dimension = (
            <FormattedMessage
                id="sb.SbRespAppliedFiltersItem.dimensionDays"
                defaultMessage=" Days"
            />
        );
    } else if (isLocalPickupFilter(filterName)) {
        value = null;
    } else if (isWhiteGloveFilter(filterName)) {
        value = whiteGloveFilterValue({ currency, filterValue });
    } else if (isStatsFilter(filterName)) {
        const [min, max] = constructStatsRange(
            urlLabel,
            get(availableValues, '[0].displayName')
        ).map(rangeValue => {
            if (isPriceFilter(filterName)) {
                return (
                    <FormattedNumber
                        value={rangeValue}
                        style="currency"
                        currency={currency}
                        minimumFractionDigits={handleMinimumFractionDigits(rangeValue)}
                    />
                );
            } else {
                return rangeValue;
            }
        });

        value = minMaxToAppliedFilterValue(min, max);
    } else if (isFreeShippingFilter(filterName)) {
        localizedFilterValue = sbSharedRefineMenuMessages.shippingOptions;
        value = sbSharedRefineMenuMessages.shippingFree;
    } else if (isRingSizeFilter(filterName)) {
        if (ringMeasurementType === RING_SIZE_MM) {
            value = filterValue?.properties?.mm;
            dimension = (
                <FormattedMessage id="sb.SbRespAppliedFiltersItem.mmRingSize" defaultMessage="mm" />
            );
        }
    }

    return (
        <div className={styles.filterItemWrapper}>
            <SearchTag
                dataTn={`filter-tag-${filterName}`}
                onDelete={onCloseClick}
                isActive={!isSbTopNavVariant}
            >
                <div className={styles.truncateLabel}>
                    <span data-tn={`label-${filterName}`}>{localizedFilterValue}</span>
                    {!!value && (
                        <Fragment>
                            <span>:</span>
                            {isColorFilter(filterName) && (
                                <span
                                    className={styles.colorDot}
                                    style={{ backgroundColor: filterValue.hexCode }}
                                />
                            )}
                            <span className={styles.value} data-tn={`value-${filterName}`}>
                                {value}
                                {dimension || ''}
                            </span>
                        </Fragment>
                    )}
                </div>
            </SearchTag>
        </div>
    );
};

SbRespAppliedFiltersItem.propTypes = {
    currency: PropTypes.string,
    filterName: PropTypes.string.isRequired,
    onCloseClick: PropTypes.func.isRequired,
    filterValue: PropTypes.object,
    availableValues: PropTypes.array.isRequired,
    measurementUnit: PropTypes.string,
    ringMeasurementType: PropTypes.string,
    localizedFilterName: PropTypes.string,
};
